import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import { organizationAPI } from '#/api/organization'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { toast } from 'sonner'


interface FormInputs {
  name: string
}

interface DeletePartnerModalProps {
  partnerId: string
  name: string
  onClose: () => void
}


export function DeletePartnerModal({ partnerId, name, onClose }: DeletePartnerModalProps) {
  const { t } = useTranslation([ 'partners' ])
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }

  // Form state
  const form = useForm<FormInputs>({
    defaultValues: {
      name: ''
    }
  })

  // Delete the partner organization
  const removePartnerMutation = useMutation({
    mutationKey: [ 'organizations', params.organization, 'partners', 'remove', partnerId ],
    mutationFn: () => organizationAPI.delete(partnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ 'organizations', params.organization, 'partners' ] })
      onClose()
      toast.success(t('partner_list.menu.toasts.remove_success'))
    },
    onError: () => {
      toast.error(t('partner_list.menu.toasts.remove_error'))
    }
  })

  return (
    <Dialog open modal onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('partner_list.menu.remove_title', { name })}</DialogTitle>
          <DialogDescription>{t('partner_list.menu.remove_description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(() => removePartnerMutation.mutate())}
            className="flex flex-col gap-4"
          >
            <FormField
              name="name"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('partner_list.menu.remove_field')}</FormLabel>
                  <FormControl>
                    <Input
                      pattern={name}
                      required
                      maxLength={128}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('partner_list.menu.remove_cancel')}
              </Button>
              <Button type="submit" variant="destructive" disabled={removePartnerMutation.isPending}>
                {t('partner_list.menu.remove_submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
